// src/pages/ProjectCovoiturage.jsx
import { useEffect, useState } from 'react';
import '../styles/ProjectPage.css';
import CaseStudyToc from '../components/CaseStudyToc';
import Lightbox from '../components/Lightbox';
import DemoVideo from '../components/DemoVideo';
import BackToTop from '../components/BackToTop';
import { projectsData } from '../data/projects';

const GALLERY = [
    { src: '/images/covoiturage/persona-sarah.jpg', alt: 'Persona Sarah, conductrice régulière' },
    { src: '/images/covoiturage/persona-marc.jpg', alt: 'Persona Marc, passager en télétravail partiel' },
    { src: '/images/covoiturage/persona-julie.jpg', alt: 'Persona Julie, responsable mobilité RSE' },
    { src: '/images/covoiturage/journey-map-marc.jpg', alt: 'Journey map de Marc cherchant un covoiturage' },
    {
        src: '/images/covoiturage/covoiturage-etude-ifop.png',
        alt: 'Étude Ifop sur le mode de transport principal utilisé pour les trajets du quotidien, 3 % pour le covoiturage'
    },
    {
        src: '/images/covoiturage/covoiturage-backlog-sprint.png',
        alt: 'Backlog priorisé MoSCoW réparti en deux sprints, avec User Stories, complexité et story points'
    },
    {
        src: '/images/covoiturage/covoiturage-avis-1.png',
        alt: 'Commentaires d’internautes sur le covoiturage : difficulté à s’organiser avec des horaires flexibles'
    },
    {
        src: '/images/covoiturage/covoiturage-avis-2.png',
        alt: 'Commentaire d’internaute : pas de journée type, difficile de se caler avec quelqu’un'
    },
    {
        src: '/images/covoiturage/covoiturage-avis-3.png',
        alt: 'Commentaire d’internaute : hors de question de dépendre de quelqu’un pour partir ou revenir'
    },
    {
        src: '/images/covoiturage/covoiturage-avis-4.png',
        alt: 'Commentaire d’internaute : ne pas vouloir s’infliger le covoiturage pour aller au travail'
    }
];

const TOC_SECTIONS = [
    {
        id: 'recherche-groupe',
        label: 'Recherche',
        children: [
            { id: 'contexte', label: 'Contexte' },
            { id: 'etude', label: 'Étude sur le covoiturage' },
            { id: 'benchmark', label: 'Benchmark concurrentiel' }
        ]
    },
    {
        id: 'conception',
        label: 'Conception',
        children: [
            { id: 'decisions', label: 'Mes décisions' },
            { id: 'retour', label: "Ce que j'en retiens" }
        ]
    },
    { id: 'demo', label: 'Démo' }
];

export default function ProjectCovoiturage() {
    const project = projectsData.find((p) => p.id === 'covoiturage');
    const [lightboxIndex, setLightboxIndex] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="project-page">
            <main className="project-main figma-project-main">
                <div className="figma-embed-wrapper">
                    <iframe
                        className="figma-embed"
                        src={project.figmaEmbedSrc}
                        title={`Maquette Figma – ${project.title}`}
                        allowFullScreen
                    />
                </div>

                <div className="project-content">
                    <h1>{project.title}</h1>
                    <p className="project-desc">{project.desc}</p>

                    {project.tech?.length > 0 && (
                        <div className="project-tech">
                            <h3>Compétences</h3>
                            <ul>
                                {project.tech.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="project-actions">
                        <a href={project.figmaUrl} target="_blank" rel="noopener noreferrer" className="back-btn">
                            Ouvrir dans Figma ↗
                        </a>
                    </div>

                    <p className="case-study-note">
                        L'écran d'onboarding est décliné avec l'identité visuelle de VusionGroup à titre
                        de démonstration du système en marque blanche, il ne s'agit pas d'une commande
                        réelle.
                    </p>
                </div>
            </main>

            <CaseStudyToc sections={TOC_SECTIONS} />

            <section className="case-study case-study--inline">
                <section className="case-study-section">
                    <h2 id="recherche-groupe">Recherche</h2>

                    <h3 id="contexte">Contexte</h3>
                    <p>
                        Une entreprise tech veut développer une solution de covoiturage domicile-travail
                        qu'elle revendra en marque blanche à ses clients. J'avais à concevoir
                        l'application mobile et à structurer le produit.
                    </p>

                    <h3 id="etude">Étude sur le covoiturage</h3>
                    <p>
                        Le covoiturage quotidien ne représente que 3 % des trajets en France, selon{' '}
                        <a
                            href="https://www.ifop.com/article/les-pratiques-de-mobilite-des-francais-et-le-potentiel-du-covoiturage-au-quotidien/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            une étude Ifop
                        </a>
                        . Les freins ne
                        sont pas techniques : perte de flexibilité, dépendance à un inconnu, peur de ne
                        pas pouvoir rentrer quand on veut. 
                    </p>

                    <figure className="decision-figure">
                        <button type="button" onClick={() => setLightboxIndex(4)}>
                            <img src={GALLERY[4].src} alt={GALLERY[4].alt} loading="lazy" />
                        </button>
                        <figcaption>
                            L'étude Ifop citée plus haut : le covoiturage ne représente que 3 % des
                            trajets du quotidien, loin derrière la voiture individuelle en solo (57 %).
                        </figcaption>
                    </figure>

                    <p>
                        Les retours utilisateurs trouvés en ligne le
                        confirment presque mot pour mot : « je veux rentrer quand je veux », « pas envie
                        de dépendre de quelqu'un ».
                    </p>

                    <figure className="decision-figure decision-figure-grid decision-figure-grid--2col">
                        {[6, 7, 8, 9].map((i) => (
                            <button type="button" key={GALLERY[i].src} onClick={() => setLightboxIndex(i)}>
                                <img src={GALLERY[i].src} alt={GALLERY[i].alt} loading="lazy" />
                            </button>
                        ))}
                        <figcaption>
                            Quelques commentaires trouvés sous un article de 20 Minutes sur le
                            covoiturage : les mêmes freins reviennent presque mot pour mot.
                        </figcaption>
                    </figure>

                    <p>
                        Un frein reste culturel : le covoiturage ne devient durable que s'il est valorisé
                        socialement dans l'entreprise, pas seulement présenté comme une contrainte
                        écologique. Sans ça, il reste marginal.
                    </p>

                    <h3 id="benchmark">Benchmark concurrentiel</h3>
                    <p>
                        Le cadre de l'entreprise résout une partie du problème (destination commune,
                        collègues plutôt qu'inconnus). Mais mon benchmark de BlaBlaCar Daily, Karos et
                        Citygo a révélé autre chose : aucune de ces solutions ne propose d'interface RH.
                        Pas de dashboard, pas de reporting. Or dans un modèle B2B, c'est le service RSE
                        qui signe le contrat, pas le salarié. J'ai quand même repris une idée de Karos :
                        leur badge de fiabilité conducteur, basé sur le taux d'annulation, a nourri ma
                        propre notation après course.
                    </p>
                    <p>C'est devenu l'axe du produit.</p>
                </section>
            </section>

            <section className="research-gallery">
                <h3>Personas</h3>
                <div className="persona-grid">
                    {GALLERY.slice(0, 3).map((img, i) => (
                        <button key={img.src} type="button" onClick={() => setLightboxIndex(i)}>
                            <img src={img.src} alt={img.alt} loading="lazy" />
                        </button>
                    ))}
                </div>

                <h3>Journey map</h3>
                <button type="button" className="journey-map-wrapper" onClick={() => setLightboxIndex(3)}>
                    <img src={GALLERY[3].src} alt={GALLERY[3].alt} loading="lazy" />
                </button>
            </section>

            <Lightbox
                images={GALLERY}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onNavigate={setLightboxIndex}
            />

            <section className="case-study case-study--continued">
                <section className="case-study-section">
                    <h2 id="conception">Conception</h2>

                    <h3 id="decisions">Mes décisions</h3>
                    <ul className="decision-list">
                        <li>
                            <strong>Concevoir pour trois profils, pas un.</strong>
                            Sarah, conductrice régulière qui redoute de devenir la chauffeuse attitrée de
                            ses collègues. Marc, passager en télétravail partiel qui craint de dépendre
                            d'un conducteur peu fiable. Julie, responsable mobilité RSE, qui doit prouver
                            l'adoption à sa direction. Le dashboard RH n'est pas une fonctionnalité
                            annexe : c'est ce qui rend le produit vendable.
                        </li>
                        <li>
                            <strong>Traiter l'adoption comme un problème de design.</strong>
                            65 % des salariés viennent seuls en voiture. Un outil ne suffit pas à changer
                            ça, il fallait un signal de confiance directement dans le produit. J'ai conçu
                            la notation après chaque course comme une preuve de fiabilité entre collègues,
                            plutôt que comme une simple formalité de fin de trajet. La notation seule ne
                            suffit pas à rassurer avant le tout premier trajet : les profils affichent
                            aussi les préférences et la personnalité de chacun, pour construire la
                            confiance avant même la première course.
                        </li>
                        <li>
                            <strong>Choisir un vert que personne n'utilise en B2B.</strong>
                            Les verts vifs signalent l'alimentaire et le bien-être. J'ai pris un vert
                            sombre désaturé pour rester crédible en contexte professionnel, validé à
                            6,64:1 de contraste sur blanc.
                        </li>
                        <li>
                            <strong>S'appuyer sur l'existant plutôt que partir de zéro.</strong>
                            Pour la typographie, j'ai choisi Poppins : lisible sur mobile, formes douces,
                            large famille de graisses adaptée à un contexte B2B moderne. Pour les
                            composants et les icônes, je me suis appuyée sur les applications de
                            covoiturage analysées en benchmark et sur des références plus larges comme
                            Uber, dont l'interface incarne un équilibre entre sobriété et expérience
                            premium : coins arrondis, icônes linéaires, composants espacés.
                        </li>
                        <li>
                            <strong>Anglais dans le système, français à l'écran.</strong>
                            Les composants et tokens sont nommés en anglais pour la maintenabilité, les
                            interfaces restent en français. C'est ce qui permet à un produit en marque
                            blanche d'être repris par une autre équipe.
                        </li>
                    </ul>

                    <p className="case-study-note">
                        <strong>Livrables : </strong>
                        Benchmark concurrentiel · 3 personas · Journey map · UI kit complet · 25 écrans
                        haute-fidélité · Prototype interactif ·{' '}
                        <a
                            href="https://app.notion.com/p/7e70b7cc32e783e993dd014155e1ad42?v=51b0b7cc32e782349a7f08d3c4fb6d53&source=copy_link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Backlog priorisé MoSCoW ↗
                        </a>{' '}
                        · Plan de sprint
                    </p>

                    <figure className="decision-figure">
                        <button type="button" onClick={() => setLightboxIndex(5)}>
                            <img src={GALLERY[5].src} alt={GALLERY[5].alt} loading="lazy" />
                        </button>
                        <figcaption>
                            Le backlog réparti sur deux sprints, priorisé en MoSCoW avec complexité et
                            story points par User Story.
                        </figcaption>
                    </figure>

                    <p>
                        Le parcours retenu pour les maquettes haute-fidélité (inscription, connexion,
                        recherche, réservation, confirmation, communication, trajet, évaluation) a été
                        choisi parce qu'il couvre le flux le plus représentatif de l'application. Plutôt
                        que de multiplier les écrans, j'ai priorisé la complétude du parcours principal,
                        en ajoutant des états secondaires seulement là où ils comptaient vraiment :
                        l'erreur de connexion, la messagerie vide. Le prototype enchaîne ces écrans avec
                        des transitions pensées pour rester naturelles sur mobile : dissolve pour les
                        changements d'état, glissement latéral pour la navigation.
                        Le dashboard RH a été pensé au niveau stratégique
                        (benchmark, personas, journey map) sans faire partie de ce périmètre à prototyper,
                        tout comme les badges et la page économies, identifiés pendant la recherche mais
                        restés au stade d'idées faute de temps (avec une idée d'export façon story
                        Instagram, dans l'esprit du récapitulatif annuel de Spotify).
                    </p>

                    <h3 id="retour">Ce que j'en retiens</h3>
                    <p>
                        Ce que j'en retiens, c'est que concevoir pour un produit B2B veut dire concevoir
                        pour deux publics à la fois : celui qui utilise l'app au quotidien, et celui qui
                        l'achète. Avant ce projet, j'aurais sans doute optimisé uniquement pour Sarah et
                        Marc. Le benchmark m'a montré que c'est justement ce que personne ne fait, et que
                        Julie, la responsable RSE, est celle qui décide si le produit existe ou non. Cette
                        bascule a influencé jusqu'aux choix les plus concrets : le vert désaturé pour
                        rester crédible en contexte professionnel, ou l'anglais dans le système pour qu'un
                        produit en marque blanche puisse être repris par une autre équipe.
                    </p>
                </section>
            </section>

            <section className="case-study case-study--continued">
                <section className="case-study-section">
                    <h2 id="demo">Démo</h2>
                    <DemoVideo
                        src="/images/covoiturage/covoiturage-demo.mp4"
                        poster="/images/preview/preview-covoiturage.jpg"
                    />
                </section>
            </section>

            <BackToTop />
        </div>
    );
}
